import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Bell, Warning, Lightning, GasPump, MapPin, Check, X, ClockClockwise, ArrowsClockwise, CaretDown, CaretUp, User } from "@phosphor-icons/react";

const TYPE_META = {
  speeding:  { label: "Speeding",       icon: Lightning, color: "bg-ps-red text-white",     badge: "bg-red-50 text-ps-red border-red-200" },
  idle:      { label: "Idle running",   icon: ClockClockwise, color: "bg-amber-500 text-white", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  fuel_drop: { label: "Fuel drop",      icon: GasPump,   color: "bg-ps-red text-white",     badge: "bg-red-50 text-ps-red border-red-200" },
};

const STATUS_BADGE = {
  open:         "bg-ps-red text-white",
  acknowledged: "bg-slate-200 text-slate-700",
  resolved:     "bg-emerald-100 text-emerald-700 border border-emerald-200",
};

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function Alerts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState({});  // { alert_id: true }
  const [driversByPlate, setDriversByPlate] = useState({});  // recently-used driver per vehicle

  const load = async () => {
    setLoading(true);
    try {
      const params = { days: 30, limit: 300 };
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get("/gps/alerts", { params });
      setRows(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterType, filterStatus]);  // eslint-disable-line

  // For any expanded row that doesn't have a driver name yet, look it up from
  // the vehicle's most recent activity — that's the closest thing to "who was
  // driving when the alert fired". Cached so we don't hammer the API on scroll.
  const ensureDriverFor = async (plate) => {
    if (!plate || plate in driversByPlate) return;
    try {
      const { data } = await api.get("/activities", { params: { vehicle: plate, limit: 1 } });
      const drv = (data && data[0] && data[0].driver_name) || "";
      setDriversByPlate((m) => ({ ...m, [plate]: drv || "—" }));
    } catch { setDriversByPlate((m) => ({ ...m, [plate]: "—" })); }
  };

  const toggleExpand = (r) => {
    setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }));
    if (!expanded[r.id]) ensureDriverFor(r.vehicle_number);
  };

  const ack = async (id) => {
    setBusy(id);
    try {
      await api.patch(`/gps/alerts/${id}/ack`);
      await load();
    } finally { setBusy(""); }
  };
  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.patch(`/gps/alerts/${id}/resolve`);
      await load();
    } finally { setBusy(""); }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length, open: 0, speeding: 0, idle: 0, fuel_drop: 0 };
    rows.forEach((r) => {
      if (r.status === "open") c.open++;
      c[r.type] = (c[r.type] || 0) + 1;
    });
    return c;
  }, [rows]);

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="alerts-page">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-ps-red font-semibold mb-1 flex items-center gap-2">
            <Bell size={14} weight="fill" /> Fleet Alerts
          </div>
          <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-ps-deep">What needs your attention</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Automated detection on GPS history: speeding, idle-with-engine-on, and fuel-tank drops while parked.
            Approvers with WhatsApp on file get pinged the moment a high-severity alert fires.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="alerts-refresh"
          className="self-start inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold"
        >
          <ArrowsClockwise size={13} weight="bold" /> Refresh
        </button>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI label="Open" value={counts.open} color="bg-ps-red text-white" testid="alerts-kpi-open" />
        <KPI label="Speeding (30d)" value={counts.speeding || 0} color="bg-white text-ps-deep border border-slate-200" icon={Lightning} testid="alerts-kpi-speeding" />
        <KPI label="Idle running (30d)" value={counts.idle || 0} color="bg-white text-ps-deep border border-slate-200" icon={ClockClockwise} testid="alerts-kpi-idle" />
        <KPI label="Fuel drops (30d)" value={counts.fuel_drop || 0} color="bg-white text-ps-deep border border-slate-200" icon={GasPump} testid="alerts-kpi-fuel" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Filters</span>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="alerts-filter-type" className="border border-slate-300 rounded px-2 py-1 text-xs">
          <option value="">All types</option>
          <option value="speeding">Speeding</option>
          <option value="idle">Idle running</option>
          <option value="fuel_drop">Fuel drop</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="alerts-filter-status" className="border border-slate-300 rounded px-2 py-1 text-xs">
          <option value="">Any status</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
            <tr>
              <th className="px-3 py-3 text-left w-32">When</th>
              <th className="px-3 py-3 text-left w-32">Type</th>
              <th className="px-3 py-3 text-left w-28">Truck</th>
              <th className="px-3 py-3 text-left">What happened</th>
              <th className="px-3 py-3 text-left w-32">Status</th>
              <th className="px-3 py-3 text-right w-44">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="alerts-table-body">
            {loading && <tr><td colSpan={6} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500">
                <Bell size={32} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                No alerts in the last 30 days. Your fleet is behaving!
              </td></tr>
            )}
            {rows.map((r) => {
              const meta = TYPE_META[r.type] || { label: r.type, icon: Warning, color: "bg-slate-200", badge: "bg-slate-50 text-slate-700 border-slate-200" };
              const Icon = meta.icon;
              const isOpen = !!expanded[r.id];
              const d = r.details || {};
              const gmapsUrl = (r.lat && r.lng) ? `https://www.google.com/maps?q=${r.lat},${r.lng}` : null;
              return (
                <Fragment key={r.id}>
                <tr className="hover:bg-slate-50" data-testid={`alert-row-${r.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    <button onClick={() => toggleExpand(r)} data-testid={`alert-expand-${r.id}`} className="flex items-start gap-1.5 text-left hover:text-ps-blue">
                      {isOpen ? <CaretUp size={12} weight="bold" className="mt-0.5 shrink-0" /> : <CaretDown size={12} weight="bold" className="mt-0.5 shrink-0" />}
                      <div>
                        <div className="font-semibold text-ps-deep">{timeAgo(r.triggered_at)}</div>
                        <div className="text-[10px] text-slate-400 font-mono-data">{String(r.triggered_at).slice(0, 16).replace("T", " ")}</div>
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${meta.badge}`}>
                      <Icon size={11} weight="bold" />
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono-data font-bold text-ps-blue">{r.vehicle_number}</td>
                  <td className="px-3 py-2">
                    <div className="text-slate-800">{r.message}</div>
                    {r.address && (
                      <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <MapPin size={10} weight="bold" /> {r.address}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGE[r.status] || ""}`}>
                      {r.status}
                    </span>
                    {r.ack_by && (
                      <div className="text-[10px] text-slate-500 mt-1 truncate" title={r.ack_by}>by {r.ack_by.split("@")[0]}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.status === "open" && (
                      <button
                        onClick={() => ack(r.id)}
                        disabled={busy === r.id}
                        data-testid={`alert-ack-${r.id}`}
                        className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 font-semibold mr-1 disabled:opacity-50"
                      >
                        <Check size={11} weight="bold" className="inline -mt-0.5 mr-1" /> Ack
                      </button>
                    )}
                    {r.status !== "resolved" && (
                      <button
                        onClick={() => resolve(r.id)}
                        disabled={busy === r.id}
                        data-testid={`alert-resolve-${r.id}`}
                        className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-semibold disabled:opacity-50"
                      >
                        <X size={11} weight="bold" className="inline -mt-0.5 mr-1" /> Resolve
                      </button>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-slate-50/70" data-testid={`alert-details-${r.id}`}>
                    <td colSpan={6} className="px-6 py-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                        {/* Driver */}
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-1"><User size={10} weight="bold" /> Driver</div>
                          <div className="font-mono-data font-bold text-ps-deep mt-0.5">{driversByPlate[r.vehicle_number] || "…"}</div>
                          <div className="text-[10px] text-slate-400">last known — confront to confirm</div>
                        </div>
                        {/* Idle duration */}
                        {r.type === "idle" && typeof d.duration_min === "number" && (
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Duration</div>
                            <div className="font-mono-data font-bold text-amber-700 mt-0.5">{d.duration_min.toFixed(0)} minutes</div>
                            <div className="text-[10px] text-slate-400">engine on, speed &lt; 2 km/h</div>
                          </div>
                        )}
                        {/* Speeding — peak + sustained */}
                        {r.type === "speeding" && (
                          <>
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Peak speed</div>
                              <div className="font-mono-data font-bold text-red-700 mt-0.5">{typeof d.peak_kmh === "number" ? `${d.peak_kmh.toFixed(0)} km/h` : "—"}</div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Sustained</div>
                              <div className="font-mono-data font-bold text-red-700 mt-0.5">{typeof d.sustained_min === "number" ? `${d.sustained_min.toFixed(0)} min` : "—"}</div>
                              <div className="text-[10px] text-slate-400">above 80 km/h</div>
                            </div>
                          </>
                        )}
                        {/* Fuel drop */}
                        {r.type === "fuel_drop" && (
                          <>
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Fuel drop</div>
                              <div className="font-mono-data font-bold text-red-700 mt-0.5">{typeof d.drop_pct === "number" ? `${d.drop_pct.toFixed(1)}%` : "—"}</div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Estimated litres</div>
                              <div className="font-mono-data font-bold text-red-700 mt-0.5">{typeof d.drop_l === "number" ? `${d.drop_l.toFixed(0)} L` : "—"}</div>
                            </div>
                          </>
                        )}
                        {/* Coordinates */}
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Coordinates</div>
                          <div className="font-mono-data text-slate-700 mt-0.5">
                            {r.lat && r.lng ? `${r.lat.toFixed(5)}, ${r.lng.toFixed(5)}` : "—"}
                          </div>
                        </div>
                      </div>
                      {/* Actions row */}
                      <div className="mt-3 flex flex-wrap gap-2 items-center">
                        {gmapsUrl && (
                          <a
                            href={gmapsUrl}
                            target="_blank" rel="noreferrer"
                            data-testid={`alert-gmaps-${r.id}`}
                            className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 px-2.5 py-1 rounded text-[11px] uppercase tracking-wider font-semibold hover:bg-white"
                          >
                            <MapPin size={11} weight="bold" /> Open in Google Maps
                          </a>
                        )}
                        <Link
                          to={`/map?vehicle=${encodeURIComponent(r.vehicle_number)}`}
                          data-testid={`alert-show-on-map-${r.id}`}
                          className="inline-flex items-center gap-1.5 bg-ps-blue text-white px-2.5 py-1 rounded text-[11px] uppercase tracking-wider font-bold hover:bg-ps-deep"
                        >
                          Show on fleet map →
                        </Link>
                        {r.ack_at && <span className="text-[10px] text-slate-500">Acknowledged {timeAgo(r.ack_at)}</span>}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-slate-400 mt-4 leading-relaxed max-w-2xl">
        Thresholds: speeding &gt; 80 km/h sustained ≥ 2 min · idle = engine on AND speed &lt; 2 km/h for ≥ 30 min · fuel drop &gt; 10 % within 30 min while parked. Same vehicle + rule is cooled-down for 60 min before re-alerting.
      </p>
    </div>
  );
}

function KPI({ label, value, color, icon: Icon, testid }) {
  return (
    <div className={`rounded-lg p-3 flex items-center gap-3 ${color}`} data-testid={testid}>
      {Icon && <Icon size={20} weight="duotone" className="opacity-70" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">{label}</div>
        <div className="font-display font-extrabold text-2xl tabular leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}
